// Thin fetch client for the nav-telemetry backend.
//
// Base URL comes from VITE_TELEMETRY_URL. When it is unset (or the PLACEHOLDER
// sentinel), every read degrades to the mock fixtures in mock.ts so the SPA
// renders with no backend. When a real URL is set and VITE_TELEMETRY_HMAC_SECRET
// is provided, requests carry the same HMAC headers the backend expects.
//
// Endpoint shapes match server/telemetry/src/index.ts:
//   GET /nav-telemetry/trips              -> { trips: TripSummary[] }
//   GET /nav-telemetry/trips/:id/report   -> TripReport
//   WS  /nav-telemetry/live?tripId=<id>   -> newline-delimited JSON events
//
// TODO(backend): GET /nav-telemetry/diagnostics does not exist yet; getDiagnostics
// always returns mock data until it lands (see types.ts DiagnosticRun).

import { authHeaders } from "./hmac.js";
import { mockDiagnostics, mockReports, mockTrips } from "./mock.js";
import type { Discrepancy, DiagnosticRun, TripReport, TripSummary } from "./types.js";

const PLACEHOLDER = "__PLACEHOLDER__";

const RAW_BASE = import.meta.env.VITE_TELEMETRY_URL ?? PLACEHOLDER;
const SECRET = import.meta.env.VITE_TELEMETRY_HMAC_SECRET ?? "";

/** Strip a trailing slash so path joins are predictable. */
function normalizeBase(base: string): string {
  return base.replace(/\/+$/, "");
}

const BASE = normalizeBase(RAW_BASE);

/** True when no real backend URL is configured -> serve mock data. */
export const isPlaceholder = RAW_BASE === PLACEHOLDER || RAW_BASE === "";

/** Whether outgoing requests will be HMAC-signed. */
export const isSigned = !isPlaceholder && SECRET.length > 0;

async function headers(): Promise<Record<string, string>> {
  const base: Record<string, string> = { accept: "application/json" };
  // TODO(read-auth): the backend does not verify HMAC on read endpoints today.
  // If a bearer/read-token scheme is chosen instead, swap this block for an
  // `authorization: Bearer <token>` header sourced from VITE_TELEMETRY_TOKEN.
  if (isSigned) Object.assign(base, await authHeaders(SECRET));
  return base;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: await headers() });
  if (!res.ok) throw new Error(`${path} -> ${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

// D1 stores discrepancy `detail` as a JSON string; the report handler returns
// rows verbatim, so normalise it back to an object here.
function normalizeDiscrepancy(d: Discrepancy): Discrepancy {
  if (typeof d.detail === "string") {
    try {
      return { ...d, detail: JSON.parse(d.detail) as Record<string, unknown> };
    } catch {
      return { ...d, detail: { raw: d.detail } };
    }
  }
  return d;
}

// --- Read API ---------------------------------------------------------------

export async function listTrips(): Promise<TripSummary[]> {
  if (isPlaceholder) return mockTrips;
  const { trips } = await getJson<{ trips: TripSummary[] }>("/nav-telemetry/trips");
  return trips;
}

export async function getTripReport(id: string): Promise<TripReport> {
  if (isPlaceholder) {
    const report = mockReports[id];
    if (!report) throw new Error(`no mock report for ${id}`);
    return report;
  }
  const report = await getJson<TripReport>(
    `/nav-telemetry/trips/${encodeURIComponent(id)}/report`,
  );
  return { ...report, discrepancies: report.discrepancies.map(normalizeDiscrepancy) };
}

export async function getDiagnostics(): Promise<DiagnosticRun[]> {
  // TODO(backend): replace with getJson("/nav-telemetry/diagnostics") once the
  // list endpoint exists. For now always mock (even against a real backend).
  return mockDiagnostics;
}

// --- Live monitor -----------------------------------------------------------

/**
 * Open the live-trip WebSocket for a session.
 *
 * The backend route is `/nav-telemetry/live?tripId=<id>` (the task's
 * `/live/:sessionId` maps to this query param). It requires an HMAC handshake,
 * but browsers CANNOT set custom headers on a WebSocket upgrade.
 *
 * TODO(backend): to make browser live-monitoring work, accept the signature via
 * a query param (`?ts=&sig=`) or a Sec-WebSocket-Protocol subprotocol instead
 * of the `x-wd-*` headers. Until then this connects unauthenticated and will be
 * rejected (401) by the current handshake check. Returns null in placeholder
 * mode.
 */
export function openLive(sessionId: string): WebSocket | null {
  if (isPlaceholder) return null;
  const wsBase = BASE.replace(/^http/, "ws");
  const url = `${wsBase}/nav-telemetry/live?tripId=${encodeURIComponent(sessionId)}`;
  return new WebSocket(url);
}
